import { useEffect, useRef, useState, type CSSProperties } from 'react';
import factor1 from '../../levels/factor/obstacles/1.png';
import factor2 from '../../levels/factor/obstacles/2.png';
import factorFoam from '../../levels/factor/obstacles/foam.png';
import {
  FACTOR_OVERLAY_FADE_MS,
  FACTOR_OVERLAY_HOLD_MS,
} from './gameplayConstants';
import { FACTOR_OVERLAY_PATHS } from './factorOverlayTargets';
import './LevelFactorOverlay.css';

type FactorPhase = 'in' | 'hold' | 'out';

const PHASE_MS: Record<FactorPhase, number> = {
  in: FACTOR_OVERLAY_FADE_MS,
  hold: FACTOR_OVERLAY_HOLD_MS,
  out: FACTOR_OVERLAY_FADE_MS,
};

const NEXT_PHASE: Record<FactorPhase, FactorPhase | null> = {
  in: 'hold',
  hold: 'out',
  out: null,
};

const FLIGHT_SEC = 2.5;
const DWELL_SEC = 0.5;
const CYCLE_SEC = (FLIGHT_SEC + DWELL_SEC) * 2;

const SRC: Record<(typeof FACTOR_OVERLAY_PATHS)[number]['srcKey'], string> = {
  factor_1: factor1,
  factor_2: factor2,
};

type SpritePose = {
  id: string;
  srcKey: (typeof FACTOR_OVERLAY_PATHS)[number]['srcKey'];
  xPct: number;
  yPct: number;
  widthPct: number;
  opacity: number;
};

function poseAt(elapsedSec: number, path: (typeof FACTOR_OVERLAY_PATHS)[number]): SpritePose {
  const t = elapsedSec - path.staggerSec;
  if (t < 0) {
    return {
      id: path.id,
      srcKey: path.srcKey,
      xPct: path.startXPct,
      yPct: path.startYPct,
      widthPct: path.widthPct,
      opacity: 0,
    };
  }
  const cycleT = t % CYCLE_SEC;
  let fromStart = true;
  let local = cycleT;
  let opacity = 0;
  if (local < FLIGHT_SEC) {
    const u = local / FLIGHT_SEC;
    fromStart = true;
    opacity = Math.sin(Math.PI * u);
    local = u;
  } else if (local < FLIGHT_SEC + DWELL_SEC) {
    fromStart = false;
    opacity = 0;
    local = 1;
  } else if (local < FLIGHT_SEC * 2 + DWELL_SEC) {
    const u = (local - FLIGHT_SEC - DWELL_SEC) / FLIGHT_SEC;
    fromStart = false;
    opacity = Math.sin(Math.PI * u);
    local = u;
  } else {
    fromStart = true;
    opacity = 0;
    local = 0;
  }
  const xPct =
    (fromStart ? path.startXPct : path.finishXPct) +
    ((fromStart ? path.finishXPct : path.startXPct) -
      (fromStart ? path.startXPct : path.finishXPct)) *
      local;
  const yPct =
    (fromStart ? path.startYPct : path.finishYPct) +
    ((fromStart ? path.finishYPct : path.startYPct) -
      (fromStart ? path.startYPct : path.finishYPct)) *
      local;
  return {
    id: path.id,
    srcKey: path.srcKey,
    xPct,
    yPct,
    widthPct: path.widthPct,
    opacity,
  };
}

type LevelFactorOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelFactorOverlay({
  isPaused,
  onComplete,
}: LevelFactorOverlayProps) {
  const [phase, setPhase] = useState<FactorPhase>('in');
  const [shown, setShown] = useState(false);
  const [poses, setPoses] = useState<SpritePose[]>(() =>
    FACTOR_OVERLAY_PATHS.map((p) => poseAt(0, p)),
  );
  const remainingMsRef = useRef(PHASE_MS.in);
  const elapsedSecRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      const dt = now - lastNow;
      lastNow = now;
      remainingMsRef.current -= dt;
      elapsedSecRef.current += dt / 1000;
      setPoses(FACTOR_OVERLAY_PATHS.map((p) => poseAt(elapsedSecRef.current, p)));
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  return (
    <div
      className={
        shown
          ? 'level-factor-overlay level-factor-overlay--shown'
          : 'level-factor-overlay'
      }
      style={
        {
          transitionDuration: `${FACTOR_OVERLAY_FADE_MS}ms`,
        } as CSSProperties
      }
      aria-hidden
    >
      <img
        className="level-factor-overlay__foam"
        src={factorFoam}
        alt=""
      />
      {poses.map((pose) => (
        <img
          key={pose.id}
          className="level-factor-overlay__sprite"
          src={SRC[pose.srcKey]}
          alt=""
          style={{
            left: `${pose.xPct}%`,
            top: `${pose.yPct}%`,
            width: `${pose.widthPct}%`,
            opacity: pose.opacity,
          }}
        />
      ))}
    </div>
  );
}
